const Trace = require('./app-diagnostics');

module.exports = { getPushPresentation };

const internals = {};

function getPushPresentation({ trace, receiver = {}, app = {} }) {

    const push = Trace.getPushSummary(trace, receiver);
    const events = trace.events || [];
    const native = trace.native?.firebaseNative || {};
    const fresh = !!trace.nativeRefreshedAt;
    const received = events.some(({ event }) => event === 'push.native-received' || event === 'push.received');
    const requested = push.requestedDestination?.pathname;
    const current = app.route?.pathname;
    const requestedSchoolId = push.requestedSchoolId;
    const schoolMatches = requestedSchoolId ? Number(app.currentSchoolId) === requestedSchoolId
        : !push.requestedDestination?.queryKeys?.includes('s');
    const completed = push.lastStage === 'navigation.dispatch' || push.lastStage === 'bootstrap.settled'
        || (push.lastStage === 'push.settled' && push.lastStageDetails?.current);
    const arrived = received && requested && requested === current && completed && schoolMatches
        && !app.bootstrapLoading && !/\[(redacted|invalid)\]/.test(requested);
    const requestedLabel = requested ? `${requested}${requestedSchoolId ? ` in community ${requestedSchoolId}` : ''}` : undefined;
    const currentSchoolId = Number(app.currentSchoolId);
    const currentLabel = current ? `${current}${Number.isSafeInteger(currentSchoolId) && currentSchoolId > 0 ? ` in community ${currentSchoolId}` : ''}` : 'not reported';
    const known = (value) => typeof value === 'boolean';
    const nativeStatus = (value) => fresh && known(value) ? value ? 'success' : 'error' : 'unknown';
    const checks = [
        { id: 'native-handler', label: 'iOS notification handler', status: nativeStatus(native.notificationDelegateHandlesResponse),
            detail: !fresh ? 'Refresh native status to check the handler.'
                : !known(native.notificationDelegateHandlesResponse) ? 'This native build does not report handler status.'
                : native.notificationDelegate ? `${native.notificationDelegate}${native.notificationDelegateHandlesResponse ? ' can receive notification taps.' : ' cannot receive notification taps.'}`
                    : 'No notification handler is registered.' },
        { id: 'js-listener', label: 'JavaScript listener',
            status: receiver.listenerRegistered ? 'success' : receiver.initialized ? 'error' : 'pending',
            detail: receiver.listenerRegistered ? 'The app is listening for Firebase notifications.' : 'Waiting for Firebase listener registration.' },
        { id: 'native-callback', label: 'Firebase native callback', status: nativeStatus(native.callbackRegistered),
            detail: !fresh ? 'Refresh native status to check the callback.'
                : !known(native.callbackRegistered) ? 'This native build does not report callback status.'
                : native.callbackRegistered ? `Callback connected; ${native.pendingCount || 0} native messages queued.` : 'Firebase has no callback connected to JavaScript.' },
        { id: 'received', label: 'Notification reached JavaScript', status: received ? 'success' : 'pending',
            detail: received ? 'A Firebase callback was recorded in this trace.' : 'No callback recorded since the last test reset.' },
        { id: 'router', label: 'App ready to navigate', status: receiver.routerReady ? 'success' : 'pending',
            detail: receiver.routerReady ? 'Authentication has settled and routing is ready.' : 'Waiting for app initialization or authentication.' },
        { id: 'destination', label: 'Requested destination', status: arrived ? 'success' : 'pending',
            detail: requestedLabel || 'No destination received.' },
        { id: 'screen', label: 'Current screen', status: 'unknown', detail: currentLabel }
    ];
    let summary = { status: 'pending', title: 'No push callback received',
        message: 'The app may have resumed, but no notification has reached JavaScript in this trace. Tap a notification, then reopen Diagnostics.' };

    if (received) {
        const waiting = {
            'auth.wait': ['Waiting for sign-in status', 'The notification arrived. Authentication must settle before opening its destination.'],
            'resume.wait': ['Restoring the app session', 'The notification arrived. The app is restoring its foreground session.'],
            'bootstrap.wait': ['Loading the community', 'The notification arrived. Community data is loading before navigation.']
        }[push.lastStage];
        summary = { status: arrived ? 'success' : 'pending', title: arrived ? 'Notification destination is open' : 'Notification received',
            message: arrived ? `The app opened ${requestedLabel}.` : requested
                ? `Requested ${requestedLabel}. The current screen is ${currentLabel}.`
                : 'Firebase delivered the notification. A destination has not been selected yet.' };
        if (waiting) {
            summary = { status: 'pending', title: waiting[0], message: waiting[1] };
        }

        if (push.lastStage === 'push.failed') {
            summary = { status: 'error', title: 'Notification handling failed', message: 'The callback arrived, but processing failed. Open the latest error in the timeline for details.' };
        }

        if (push.lastStage === 'push.ignored') {
            const untapped = push.lastStageDetails?.reason === 'not-a-tap';
            summary = { status: untapped ? 'unknown' : 'error', title: untapped ? 'Notification arrived without a tap' : 'Notification destination was rejected',
                message: untapped ? 'A notification delivery was recorded without tap metadata, so the app did not navigate.' : 'The payload did not contain a supported app destination. Inspect the latest notification event.' };
        }
    }
    else if (fresh && native.pluginAvailable === false) {
        summary = { status: 'error', title: 'Native Firebase is unavailable', message: 'The native Firebase plugin was not loaded. Check the installed iOS build.' };
    }
    else if (fresh && native.fcmEnabled === false) {
        summary = { status: 'error', title: 'Native Firebase messaging is disabled', message: 'The installed native configuration has FCM disabled. No settings have been changed by Diagnostics.' };
    }
    else if (fresh && native.notificationDelegateHandlesResponse === false) {
        summary = { status: 'error', title: 'iOS native notification handler is missing', message: 'iOS has no handler capable of receiving notification taps. The native delegate check in the timeline shows what was registered.' };
    }
    else if (fresh && native.callbackRegistered === false && receiver.listenerRegistered) {
        summary = { status: 'error', title: 'Firebase native callback is not connected', message: 'JavaScript attempted to register its listener, but Firebase has no native callback registered. Notification data cannot cross that boundary.' };
    }

    return { summary, checks, events: events.map(internals.describeEvent) };
}

internals.describeEvent = (entry) => {

    const details = entry.details || {};
    const path = details.destination?.pathname || details.route?.pathname;
    const labels = {
        scene_became_active: ['App entered the foreground', 'Background false is expected after activation; this event does not indicate a push was received.'],
        scene_entered_background: ['App entered the background', 'The native background state was updated.'],
        'push.native-received': ['Firebase delivered a notification to JavaScript', details.tap ? `Tap metadata: ${details.tap}.` : 'No tap metadata was supplied.'],
        'push.received': ['App received the notification payload', details.type ? `Notification type: ${details.type}.` : 'Notification type was not supplied.'],
        'push.queued': ['Notification is waiting for app readiness', 'The callback arrived before routing was ready.'],
        'push.destination': ['Notification destination selected', path],
        'auth.wait': ['Waiting for sign-in status'], 'auth.ready': ['Sign-in status resolved'],
        'resume.wait': ['Restoring the app session'], 'resume.ready': ['App session restored'],
        'bootstrap.wait': ['Loading community data'], 'bootstrap.settled': ['Community loading finished'],
        'navigation.dispatch': ['Navigation requested', path], 'navigation.committed': ['Screen changed', path],
        'push.settled': ['Notification processing finished', details.current === false ? 'A newer notification took priority.' : undefined],
        'push.delivered': ['JavaScript notification handler finished', 'Check the current screen to confirm navigation.'],
        'push.failed': ['Notification processing failed', details.stage || details.code || details.name],
        'push.ignored': ['Notification was not opened', details.reason],
        'test.router': ['Manual router test started', 'This tests navigation independently of notification delivery.'],
        'trace.cleared': ['New diagnostic test started'],
        firebase_delegates_checked: ['Native Firebase handlers checked', {
            repaired_missing_delegate: 'Restored the missing iOS notification handler after Firebase initialized.',
            repaired_firebase_placeholder: 'Replaced a Firebase permission placeholder with the notification tap handler.',
            already_configured: 'The Firebase notification handler is already connected.',
            existing_delegate_preserved: 'An existing notification handler was preserved. See its class and capabilities in technical details.',
            plugin_unavailable: 'The native Firebase plugin is unavailable.',
            fcm_disabled: 'FCM is disabled in native configuration.',
            app_delegate_handler_unavailable: 'The native app delegate cannot receive notification tap callbacks.'
        }[details.reason]]
    };
    const [title, detail] = labels[entry.event] || [];
    return { ...entry, ...(title ? { title, detail } : {}),
        status: /failed|rejected/.test(entry.event) ? 'error' : entry.event.endsWith('.wait') ? 'pending' : undefined };
};
